/**
 * VeChain API Service
 *
 * Provides access to VeChain account data via the VeChain Thor public API.
 * No API key required.
 *
 * Documentation: https://docs.vechain.org/
 */

import { logger } from './logger';
import { callCoinGeckoAPI } from './api';
import type {
  VeChainAccount,
  VeChainTransaction,
  VeChainBlock,
  VeChainWalletData,
  VeChainTokenWithInfo,
  VeChainVTHOGeneration,
} from '../types/vechain.types';

// VeChain Thor public nodes
const VECHAIN_NODE_URL = 'https://mainnet.veblocks.net';

// Wei to VET/VTHO conversion (1 VET = 10^18 wei)
const WEI_TO_VET = 1e18;

// VTHO generation rate: 0.000432 VTHO per VET per day
const VTHO_GENERATION_RATE = 0.000432;

// Request timeout in ms
const REQUEST_TIMEOUT_MS = 15000;

// Rate limiting
const REQUEST_DELAY_MS = 100;

/**
 * Delay helper for rate limiting
 */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetch with timeout
 */
const fetchWithTimeout = async (url: string): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
      }
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

/**
 * Convert hex wei to VET/VTHO
 */
export const hexWeiToVet = (hexWei: string): number => {
  return parseInt(hexWei, 16) / WEI_TO_VET;
};

/**
 * Validate VeChain address format
 */
export const isValidVechainAddress = (address: string): boolean => {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

/**
 * Calculate VTHO generation for a VET balance
 */
export const calculateVTHOGeneration = (vetBalance: number): VeChainVTHOGeneration => {
  const dailyVTHO = vetBalance * VTHO_GENERATION_RATE;
  return {
    vetBalance,
    dailyVTHO,
    monthlyVTHO: dailyVTHO * 30,
    yearlyVTHO: dailyVTHO * 365,
  };
};

/**
 * Determine economic node type based on VET balance
 */
const getNodeType = (vetBalance: number): string | null => {
  if (vetBalance >= 25000000) return 'Mjolnir X Node';
  if (vetBalance >= 15000000) return 'Thunder X Node';
  if (vetBalance >= 5000000) return 'Strength X Node';
  if (vetBalance >= 1000000) return 'VeThor X Node';
  if (vetBalance >= 30000000) return 'Mjolnir Node';
  if (vetBalance >= 15000000) return 'Thunder Node';
  if (vetBalance >= 5000000) return 'Strength Node';
  return null;
};

/**
 * VeChain API Service
 */
export const vechainAPI = {
  /**
   * Get account information
   */
  async getAccount(address: string): Promise<VeChainAccount | null> {
    try {
      if (!isValidVechainAddress(address)) {
        logger.warn('VeChainAPI', `Invalid address format: ${address}`);
        return null;
      }

      const response = await fetchWithTimeout(`${VECHAIN_NODE_URL}/accounts/${address}`);

      if (!response.ok) {
        if (response.status === 404) {
          logger.warn('VeChainAPI', `Account not found: ${address}`);
          return null;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('VeChainAPI', `Failed to fetch account ${address}`, error);
      throw error;
    }
  },

  /**
   * Get account transactions
   * Note: Thor API doesn't provide transaction history directly
   * We would need to use a block explorer API for this
   */
  async getTransactions(
    address: string,
    limit: number = 50
  ): Promise<VeChainTransaction[]> {
    try {
      // VeBlocks explorer API for transaction history
      const response = await fetchWithTimeout(
        `https://explore.vechain.org/api/accounts/${address}/transactions?count=${limit}`
      );

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      return data.transactions || [];
    } catch (error) {
      logger.error('VeChainAPI', `Failed to fetch transactions for ${address}`, error);
      return [];
    }
  },

  /**
   * Get current block
   */
  async getCurrentBlock(): Promise<VeChainBlock | null> {
    try {
      const response = await fetchWithTimeout(`${VECHAIN_NODE_URL}/blocks/best`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('VeChainAPI', 'Failed to fetch current block', error);
      return null;
    }
  },

  /**
   * Get VET and VTHO prices from CoinGecko
   */
  async getPrices(): Promise<{ vetPrice: number; vthoPrice: number }> {
    try {
      const data = await callCoinGeckoAPI('/simple/price', {
        ids: 'vechain,vethor-token',
        vs_currencies: 'usd'
      });
      return {
        vetPrice: data['vechain']?.usd || 0,
        vthoPrice: data['vethor-token']?.usd || 0,
      };
    } catch (error) {
      logger.error('VeChainAPI', 'Failed to fetch prices', error);
      return { vetPrice: 0, vthoPrice: 0 };
    }
  },

  /**
   * Get complete wallet data for an address
   */
  async getWalletData(
    address: string,
    vetPrice: number,
    vthoPrice: number
  ): Promise<VeChainWalletData | null> {
    try {
      // Fetch account info
      const account = await this.getAccount(address);
      if (!account) {
        return null;
      }

      await delay(REQUEST_DELAY_MS);

      // Fetch transactions (may be limited by explorer API)
      const transactions = await this.getTransactions(address, 25);

      // Calculate balances
      const vetBalance = hexWeiToVet(account.balance);
      const vthoBalance = hexWeiToVet(account.energy);

      // Calculate VTHO generation
      const vthoGeneration = calculateVTHOGeneration(vetBalance);

      // Determine node type
      const nodeType = getNodeType(vetBalance);

      return {
        address,
        vetBalance,
        vetBalanceUsd: vetBalance * vetPrice,
        vthoBalance,
        vthoBalanceUsd: vthoBalance * vthoPrice,
        totalBalanceUsd: (vetBalance * vetPrice) + (vthoBalance * vthoPrice),
        dailyVTHOGeneration: vthoGeneration.dailyVTHO,
        monthlyVTHOGeneration: vthoGeneration.monthlyVTHO,
        nodeType,
        nodeLevel: nodeType ? 1 : null,
        isAuthorityNode: false, // Would need additional API call
        tokens: [], // VIP180 tokens would need contract calls
        recentTransactions: transactions,
        lastUpdated: Date.now(),
      };
    } catch (error) {
      logger.error('VeChainAPI', `Failed to get wallet data for ${address}`, error);
      return null;
    }
  },

  /**
   * Get aggregated data for multiple addresses
   */
  async getMultipleWalletsData(
    addresses: string[],
    vetPrice: number,
    vthoPrice: number
  ): Promise<VeChainWalletData[]> {
    const results: VeChainWalletData[] = [];

    for (const address of addresses) {
      const walletData = await this.getWalletData(address, vetPrice, vthoPrice);
      if (walletData) {
        results.push(walletData);
      }
      await delay(500);
    }

    return results;
  },

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: number): Date {
    return new Date(timestamp * 1000);
  }
};

export default vechainAPI;
